import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {AntibodyFormat} from 'antibodies/AntibodyTypedefs';
import {
  NdcGermlineConfig,
  NdcSpreadsheetComponentFormatType,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import NestedCreateAndRegisterAntibodiesSpreadsheetImportModal from 'antibodies/nested-data-creation/spreadsheet-import/NestedCreateAndRegisterAntibodiesSpreadsheetImportModal';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {RegistryLabelingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import legacyRunModal from 'ui-library/legacyRunModal';
import * as LoadingScreen from 'ui-library/LoadingScreen';

export interface ProteinCreationSettingsData {
  selectedFormat: AntibodyFormat;
  selectedSchemaId: string;
  selectedSchemaName: string;
  selectedRegistryId: string;
  selectedStartingType: NdcSpreadsheetComponentFormatType;
  germlineConfig: NdcGermlineConfig;
  namingStrategy: RegistryLabelingStrategy | null;
}

const SETTINGS_KEY = 'protein-creation-settings';

const ProteinCreationSettingsGlobalState = makeUseGlobalClientState<ProteinCreationSettingsData | null>(null);

export function setProteinCreationSettings(data: ProteinCreationSettingsData): void {
  ProteinCreationSettingsGlobalState.updateState(SETTINGS_KEY, data);
}

export function getProteinCreationSettings(): ProteinCreationSettingsData | null {
  return ProteinCreationSettingsGlobalState.getState(SETTINGS_KEY);
}

export function clearProteinCreationSettings(): void {
  ProteinCreationSettingsGlobalState.deleteState(SETTINGS_KEY);
}

export function useProteinCreationSettings(): [
  ProteinCreationSettingsData | null,
  (data: ProteinCreationSettingsData | null) => void,
] {
  return ProteinCreationSettingsGlobalState.useGlobalClientState(SETTINGS_KEY);
}

/**
 * Whether two sets of protein creation settings would produce the same creation flow. The schema name is
 * not compared since it is derived from the schema ID.
 */
export function areProteinCreationSettingsEqual(
  a: ProteinCreationSettingsData,
  b: ProteinCreationSettingsData
): boolean {
  return (
    a.selectedFormat.id === b.selectedFormat.id &&
    a.selectedSchemaId === b.selectedSchemaId &&
    a.selectedRegistryId === b.selectedRegistryId &&
    a.selectedStartingType === b.selectedStartingType &&
    isEqual(a.germlineConfig, b.germlineConfig) &&
    a.namingStrategy === b.namingStrategy
  );
}

/**
 * Whether moving between two sets of protein creation settings requires resetting the creation flow. The
 * schema and starting type determine the table's columns; the schema also determines the format.
 */
export function doProteinCreationSettingsRequireReset(
  previous: ProteinCreationSettingsData,
  next: {selectedSchemaId: string | null; selectedStartingType: NdcSpreadsheetComponentFormatType}
): boolean {
  return (
    previous.selectedSchemaId !== next.selectedSchemaId ||
    previous.selectedStartingType !== next.selectedStartingType
  );
}

/**
 * Opens the protein creation settings modal if no settings are already set.
 * If the user completes step 1, the modal writes settings.
 * Returns whether the user submitted the protein creation settings.
 */
export async function handleOpenProteinCreationFlow(): Promise<boolean> {
  if (getProteinCreationSettings() != null) {
    return true;
  }
  LoadingScreen.init();
  await legacyRunModal(NestedCreateAndRegisterAntibodiesSpreadsheetImportModal, {
    analyticsMetadata: {
      actionOrigin: ActionOrigin.ANTIBODY_NESTED_DATA_CREATION,
      eventGroupId: analytics.makeAnalyticsEventId(),
    },
  });
  return getProteinCreationSettings() != null;
}
